import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { FiDownload, FiX, FiCheckCircle, FiClock, FiSearch } from 'react-icons/fi';
import * as XLSX from 'xlsx';
import API from '../utils/api';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import { canStaffAccess } from '../utils/staffPages';
import { fmtPercent, fyOf } from '../utils/tds';
import './SalesOrders.css';

/**
 * TDS Register — every TDS deduction customers made on our receivables.
 *
 * Source: /api/tds (client_receipt_tds). One row per receipt + bill.
 * Here you can filter by financial year / quarter / customer / certificate status,
 * track Form 16A certificates (pending → received) and export to Excel.
 * TDS is a tax credit we claim later — it is not bank money.
 */

const money = (n) =>
  `₹${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmt = (d) =>
  d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const QUARTERS = [
  { v: '', l: 'Full year' },
  { v: '1', l: 'Q1 (Apr–Jun)' },
  { v: '2', l: 'Q2 (Jul–Sep)' },
  { v: '3', l: 'Q3 (Oct–Dec)' },
  { v: '4', l: 'Q4 (Jan–Mar)' },
];

// Columns hidden for now — set to true to show them again
const SHOW_SELECT_COL  = false;   // row checkboxes (bulk update)
const SHOW_SECTION_COL = false;
const SHOW_CERT_COL    = false;   // certificate status column

const labelSt = { display: 'block', fontSize: 11, fontWeight: 700, color: '#6b7280', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.04em' };
const inputSt = { width: '100%', boxSizing: 'border-box', padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 13, outline: 'none', background: '#fff' };

/* ── File download helpers ──────────────────────────────────────── */
// Download a Blob with our own <a download> — works in every browser, unlike the
// library's built-in XLSX.writeFile which some browsers / in-app webviews block.
const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 1500);
};

// CSV fallback (opens in Excel). UTF-8 BOM so ₹ / names show correctly.
const toCsv = (rows) => {
  const cols = [];
  rows.forEach((r) => Object.keys(r).forEach((k) => { if (!cols.includes(k)) cols.push(k); }));
  const esc = (v) => { const t = v == null ? '' : String(v); return /[",\n]/.test(t) ? `"${t.replace(/"/g, '""')}"` : t; };
  return [cols.map(esc).join(','), ...rows.map((r) => cols.map((c) => esc(r[c])).join(','))].join('\r\n');
};

/* ── Certificate modal (single row or bulk) ─────────────────────── */
const CertificateModal = ({ ids, initial, onClose, onSaved }) => {
  const [status, setStatus] = useState(initial?.certificateStatus || 'received');
  const [number, setNumber] = useState(initial?.certificateNumber || '');
  const [date, setDate]     = useState(initial?.certificateDate ? String(initial.certificateDate).slice(0, 10) : '');
  const [saving, setSaving] = useState(false);
  const bulk = ids.length > 1;

  const save = async () => {
    try {
      setSaving(true);
      const body = { certificateStatus: status, certificateNumber: number, certificateDate: date || null };
      if (bulk) await API.put('/tds/certificate/bulk', { ids, ...body });
      else await API.put(`/tds/${ids[0]}/certificate`, body);
      toast.success(bulk ? `${ids.length} entries updated` : 'Certificate updated');
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not update');
    } finally { setSaving(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: '#fff', borderRadius: 14, padding: 24, width: 440, maxWidth: '95vw', boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>
            TDS certificate {bulk ? `· ${ids.length} entries` : ''}
          </h3>
          <button onClick={onClose} style={{ background: '#f3f4f6', border: 'none', borderRadius: 8, padding: '6px 8px', cursor: 'pointer' }}><FiX size={16} /></button>
        </div>

        <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
          {[['received', 'Certificate received'], ['pending', 'Pending']].map(([v, l]) => (
            <button key={v} onClick={() => setStatus(v)}
              style={{ flex: 1, padding: '8px 10px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                border: `1.5px solid ${status === v ? (v === 'received' ? '#16a34a' : '#d97706') : '#e5e7eb'}`,
                background: status === v ? (v === 'received' ? '#dcfce7' : '#fef3c7') : '#fff',
                color: status === v ? (v === 'received' ? '#15803d' : '#b45309') : '#6b7280' }}>
              {l}
            </button>
          ))}
        </div>

        {status === 'received' && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={labelSt}>Certificate no. (optional)</label>
              <input style={inputSt} value={number} onChange={(e) => setNumber(e.target.value)} placeholder="Form 16A no." maxLength={100} />
            </div>
            <div>
              <label style={labelSt}>Received on (optional)</label>
              <input type="date" style={inputSt} value={date} onChange={(e) => setDate(e.target.value)} />
            </div>
          </div>
        )}

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 }}>
          <button onClick={onClose} style={{ padding: '9px 18px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#f9fafb', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>Cancel</button>
          <button onClick={save} disabled={saving}
            style={{ padding: '9px 18px', borderRadius: 8, border: 'none', background: 'var(--primary, #6366f1)', color: '#fff', fontWeight: 600, fontSize: 13, cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1 }}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ── Page ───────────────────────────────────────────────────────── */
const AdminTds = () => {
  const { user, isAdmin } = useAuth();
  const canEdit = isAdmin || canStaffAccess(user?.allowedPages, 'tds', 'edit');

  const todayFy = useMemo(() => fyOf(new Date().toISOString().slice(0, 10)), []);
  const [fy, setFy]           = useState(todayFy);
  const [quarter, setQuarter] = useState('');
  const [status, setStatus]   = useState('all');
  const [q, setQ]             = useState('');
  const [qInput, setQInput]   = useState('');
  const [page, setPage]       = useState(1);

  const [rows, setRows]       = useState([]);
  const [years, setYears]     = useState([todayFy]);
  const [pages, setPages]     = useState(1);
  const [count, setCount]     = useState(0);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [modal, setModal]     = useState(null);   // { ids, initial }
  const [exporting, setExporting] = useState(false);

  const params = useCallback((extra = {}) => ({
    ...(fy ? { fy } : {}),
    ...(fy && quarter ? { quarter } : {}),
    certificateStatus: status,
    ...(q ? { q } : {}),
    ...extra,
  }), [fy, quarter, status, q]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await API.get('/tds', { params: params({ page, limit: 25 }) });
      setRows(data.rows || []);
      setPages(data.pages || 1);
      setCount(data.count || 0);
      if (data.financialYears?.length) setYears(data.financialYears);
      setSelected(new Set());
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load TDS register');
    } finally { setLoading(false); }
  }, [params, page]);

  useEffect(() => { load(); }, [load]);

  // search box → debounce
  useEffect(() => {
    const t = setTimeout(() => { setPage(1); setQ(qInput.trim()); }, 350);
    return () => clearTimeout(t);
  }, [qInput]);

  const toggle = (id) => setSelected((prev) => {
    const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n;
  });
  const allOnPage = rows.length > 0 && rows.every((r) => selected.has(r.id));
  const toggleAll = () => setSelected(allOnPage ? new Set() : new Set(rows.map((r) => r.id)));

  /* ── Excel export (all rows matching the filters) ── */
  const exportExcel = async () => {
    setExporting(true);
    let sheetRows = [];
    const baseName = `TDS-Register-${fy || 'all'}${quarter ? `-Q${quarter}` : ''}`;
    try {
      const { data } = await API.get('/tds', { params: params({ page: 1, limit: 5000 }) });
      const list = data.rows || [];
      if (!list.length) { toast('Nothing to export for these filters'); return; }
      sheetRows = list.map((r) => ({
        'Financial Year': r.financialYear,
        'Quarter': `Q${r.quarter}`,
        'Date': r.receiptDate,
        'Receipt No': r.receiptNumber,
        'Bill No': r.billNumber,
        'Customer': r.companyName || r.customerName,
        'Customer PAN': r.customerPan || '',
        'Section': r.tdsSection || '',
        'TDS %': r.tdsPercent == null ? '' : Number(Number(r.tdsPercent).toFixed(2)),
        'Taxable Base': Number(r.baseAmount),
        'TDS Amount': Number(r.tdsAmount),
        'Certificate': r.certificateStatus === 'received' ? 'Received' : 'Pending',
        'Certificate No': r.certificateNumber || '',
        'Certificate Date': r.certificateDate || '',
      }));
      sheetRows.push({
        'Financial Year': 'TOTAL',
        'Taxable Base': Number(data.totals?.totalBase || 0),
        'TDS Amount': Number(data.totals?.totalTds || 0),
      });
    } catch (err) {
      console.error('TDS export — could not load data', err);
      toast.error(err.response?.data?.message || `Could not load data for export: ${err.message}`);
      setExporting(false);
      return;
    }

    try {
      const ws = XLSX.utils.json_to_sheet(sheetRows);
      ws['!cols'] = [10, 8, 12, 12, 14, 28, 14, 10, 8, 14, 14, 12, 16, 14].map((wch) => ({ wch }));
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'TDS Register');
      const out = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      downloadBlob(
        new Blob([out], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
        `${baseName}.xlsx`,
      );
      toast.success('Excel file downloaded');
    } catch (err) {
      // Safety net: never leave the user without a file — fall back to CSV (opens in Excel)
      console.error('TDS export — Excel build failed, falling back to CSV', err);
      try {
        downloadBlob(new Blob(['\uFEFF' + toCsv(sheetRows)], { type: 'text/csv;charset=utf-8;' }), `${baseName}.csv`);
        toast.error(`Excel export failed (${err.message}). Downloaded as CSV instead.`);
      } catch (err2) {
        toast.error(`Export failed: ${err2.message}`);
      }
    } finally { setExporting(false); }
  };

  // number of visible columns (for the empty / loading rows)
  const colCount = 5 + 3 + (canEdit && SHOW_SELECT_COL ? 1 : 0) + (SHOW_SECTION_COL ? 1 : 0)
    + (SHOW_CERT_COL ? 1 : 0) + (canEdit ? 1 : 0);

  return (
    <div className="so-page">
      <div className="so-page-head">
        <div>
          <h2 style={{ margin: 0 }}>TDS Register</h2>
          {/* <div style={{ fontSize: 13, color: '#6b7280', marginTop: 4 }}>
            Tax deducted by customers on your bills. TDS settles the bill but is not bank money — track the certificates so you can claim it.
          </div> */}
        </div>
        <button onClick={exportExcel} disabled={exporting}
          style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '9px 16px', borderRadius: 8, border: '1px solid #d1d5db', background: '#f9fafb', fontSize: 13, fontWeight: 600, cursor: exporting ? 'not-allowed' : 'pointer', color: '#374151' }}>
          <FiDownload size={14} /> {exporting ? 'Exporting…' : 'Export Excel'}
        </button>
      </div>

      {/* Filters */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 12, marginBottom: 16, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: 14 }}>
        <div>
          <label style={labelSt}>Financial year</label>
          <select style={inputSt} value={fy} onChange={(e) => { setFy(e.target.value); setPage(1); }}>
            <option value="">All years</option>
            {years.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
        <div>
          <label style={labelSt}>Quarter</label>
          <select style={inputSt} value={quarter} disabled={!fy} onChange={(e) => { setQuarter(e.target.value); setPage(1); }}>
            {QUARTERS.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
          </select>
        </div>
        <div>
          <label style={labelSt}>Certificate</label>
          <select style={inputSt} value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }}>
            <option value="all">All</option>
            <option value="pending">Pending</option>
            <option value="received">Received</option>
          </select>
        </div>
        <div>
          <label style={labelSt}>Customer / receipt / bill</label>
          <div style={{ position: 'relative' }}>
            <FiSearch size={14} style={{ position: 'absolute', left: 10, top: 11, color: '#9ca3af' }} />
            <input style={{ ...inputSt, paddingLeft: 30 }} value={qInput} onChange={(e) => setQInput(e.target.value)} placeholder="Search…" />
          </div>
        </div>
      </div>

      {/* Bulk bar */}
      {canEdit && selected.size > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: '#eef2ff', border: '1px solid #c7d2fe', borderRadius: 10, marginBottom: 12, flexWrap: 'wrap' }}>
          <strong style={{ fontSize: 13, color: '#4338ca' }}>{selected.size} selected</strong>
          <button onClick={() => setModal({ ids: [...selected], initial: { certificateStatus: 'received' } })}
            style={{ padding: '6px 12px', borderRadius: 8, border: 'none', background: '#16a34a', color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
            Update certificate…
          </button>
          <button onClick={() => setSelected(new Set())}
            style={{ padding: '6px 12px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
            Clear
          </button>
        </div>
      )}

      {/* Table */}
      <div className="so-table-scroll" style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10 }}>
        <table className="so-table" style={{ minWidth: 980 }}>
          <thead>
            <tr>
              {canEdit && SHOW_SELECT_COL && <th style={{ width: 34 }}><input type="checkbox" checked={allOnPage} onChange={toggleAll} /></th>}
              <th>Date</th><th>Receipt</th><th>Bill</th><th>Customer</th>
              {SHOW_SECTION_COL && <th>Section</th>}
              <th style={{ textAlign: 'right' }}>%</th>
              <th style={{ textAlign: 'right' }}>Taxable value</th>
              <th style={{ textAlign: 'right' }}>TDS</th>
              {SHOW_CERT_COL && <th>Certificate</th>}
              {canEdit && <th />}
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={colCount} className="so-empty">Loading…</td></tr>}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={colCount} className="so-empty">
                No TDS entries for these filters. TDS is added when you record a payment against a bill.
              </td></tr>
            )}
            {!loading && rows.map((r) => (
              <tr key={r.id}>
                {canEdit && SHOW_SELECT_COL && <td><input type="checkbox" checked={selected.has(r.id)} onChange={() => toggle(r.id)} /></td>}
                <td style={{ whiteSpace: 'nowrap' }}>
                  {fmt(r.receiptDate)}
                  <div style={{ fontSize: 11, color: '#9ca3af' }}>FY {r.financialYear} · Q{r.quarter}</div>
                </td>
                <td><span style={{ padding: '3px 10px', borderRadius: 6, fontSize: 12, fontWeight: 700, background: '#ede9fe', color: '#6d28d9', whiteSpace: 'nowrap' }}>{r.receiptNumber}</span></td>
                <td className="so-strong">{r.billNumber || '—'}</td>
                <td>
                  <div style={{ fontWeight: 600 }}>{r.companyName || r.customerName}</div>
                  <div style={{ fontSize: 11, color: r.customerPan ? '#6b7280' : '#d97706' }}>
                    {r.customerPan ? `PAN ${r.customerPan}` : 'PAN not on file'}
                  </div>
                </td>
                {SHOW_SECTION_COL && <td>{r.tdsSection || <span className="so-muted">—</span>}</td>}
                <td style={{ textAlign: 'right' }}>{r.tdsPercent == null ? '—' : `${fmtPercent(r.tdsPercent)}%`}</td>
                <td style={{ textAlign: 'right' }}>{money(r.baseAmount)}</td>
                <td style={{ textAlign: 'right', fontWeight: 700, color: '#b45309' }}>{money(r.tdsAmount)}</td>
                {SHOW_CERT_COL && (
                <td>
                    {r.certificateStatus === 'received' ? (
                      <div>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 10px', borderRadius: 99, fontSize: 11, fontWeight: 700, background: '#dcfce7', color: '#15803d' }}>
                          <FiCheckCircle size={11} /> Received
                        </span>
                        {(r.certificateNumber || r.certificateDate) && (
                          <div style={{ fontSize: 11, color: '#6b7280', marginTop: 3 }}>
                            {r.certificateNumber}{r.certificateNumber && r.certificateDate ? ' · ' : ''}{r.certificateDate ? fmt(r.certificateDate) : ''}
                          </div>
                        )}
                      </div>
                    ) : (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 10px', borderRadius: 99, fontSize: 11, fontWeight: 700, background: '#fef3c7', color: '#b45309' }}>
                        <FiClock size={11} /> Pending
                      </span>
                    )}
                  </td>
                )}
                {canEdit && (
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <button onClick={() => setModal({ ids: [r.id], initial: r })}
                      style={{ padding: '4px 10px', background: '#eef2ff', color: '#4f46e5', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                      Update
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 12, marginTop: 14, fontSize: 13 }}>
          <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)}
            style={{ padding: '6px 12px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', cursor: page <= 1 ? 'not-allowed' : 'pointer' }}>← Prev</button>
          <span>Page {page} of {pages} · {count} entries</span>
          <button disabled={page >= pages} onClick={() => setPage((p) => p + 1)}
            style={{ padding: '6px 12px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', cursor: page >= pages ? 'not-allowed' : 'pointer' }}>Next →</button>
        </div>
      )}

      {modal && (
        <CertificateModal ids={modal.ids} initial={modal.initial}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); load(); }} />
      )}
    </div>
  );
};

export default AdminTds;